import React from 'react';
import { Grid3x3 } from 'lucide-react';

/**
 * VendorHeroGallery Component
 * Displays vendor portfolio images in a hero gallery layout.
 * This component is specifically designed to show only portfolio images (image_type='portfolio').
 * Returns null if no portfolio images are provided.
 */
const VendorHeroGallery = ({ images = [], vendorName = '', onShowAll }) => {
  // If no portfolio images, return null (blank)
  if (!images || images.length === 0) {
    return null;
  }

  // Use provided portfolio images
  const displayImages = images;

  const showAllPhotos = () => {
    if (onShowAll) {
      onShowAll();
    }
  };

  return (
    <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[500px] rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl rounded-br-2xl overflow-hidden">
      <div className="grid grid-cols-4 gap-1.5 h-full">
        {/* Main Large Image - Left Side */}
        <div className="col-span-4 md:col-span-2 relative overflow-hidden bg-gray-200 animate-pulse h-full flex items-center justify-center">
          <img
            src={displayImages[0]}
            alt={vendorName}
            className="min-w-full min-h-full w-full h-full object-cover object-center"
            style={{ 
              objectFit: 'cover',
              objectPosition: 'center'
            }}
            loading="eager"
            onLoad={(e) => {
              e.target.parentElement.classList.remove('animate-pulse', 'bg-gray-200');
            }}
            onError={(e) => {
              e.target.parentElement.classList.remove('animate-pulse');
              e.target.parentElement.classList.add('bg-gray-100');
            }}
          />
        </div>

        {/* Thumbnail Grid - Right Side (4 images in 2x2) */}
        <div className="hidden md:grid md:col-span-2 grid-cols-2 gap-1.5 h-full">
          {displayImages.slice(1, 5).map((image, index) => {
            // Only add border to corners: index 0 (top-right) and index 3 (bottom-right)
            const isCorner = index === 0 || index === 3;
            return (
              <div
                key={index}
                className={`relative overflow-hidden bg-gray-200 animate-pulse h-full flex items-center justify-center ${isCorner ? ' ' : ''}`}
              >
                <img
                  src={image}
                  alt={`${vendorName} - ${index + 2}`}
                  className="min-w-full min-h-full w-full h-full object-cover object-center"
                  style={{ 
                    objectFit: 'cover',
                    objectPosition: 'center'
                  }}
                  loading="lazy"
                  onLoad={(e) => {
                    e.target.parentElement.classList.remove('animate-pulse', 'bg-gray-200');
                  }}
                  onError={(e) => {
                    e.target.parentElement.classList.remove('animate-pulse');
                    e.target.parentElement.classList.add('bg-gray-100');
                  }}
                />
                {index === 3 && displayImages.length > 5 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      showAllPhotos();
                    }}
                    className="absolute inset-0 bg-black/50 hover:bg-black/60 flex items-center justify-center text-white font-medium transition-colors"
                  >
                    <div className="text-center">
                      <Grid3x3 className="w-6 h-6 mx-auto mb-1" />
                      <span className="text-sm">Show all {displayImages.length} photos</span>
                    </div>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile: Show all photos button */}
      {displayImages.length > 1 && (
        <button
          onClick={showAllPhotos}
          className="absolute bottom-4 right-4 bg-white text-black px-4 py-2 rounded-lg font-medium shadow-lg hover:bg-gray-50 transition-colors flex items-center gap-2 md:hidden"
        >
          <Grid3x3 className="w-4 h-4" />
          <span>Show all {displayImages.length} photos</span>
        </button>
      )}
    </div>
  );
};

export default VendorHeroGallery;
